import { useMemo } from 'react';
import * as THREE from 'three';
import { Float, Edges } from '@react-three/drei';
import type { ContainerStatus } from '../types/import-tracking.types';
import { CONTAINER_STATUS_3D_COLORS } from '../utils/container-colors';

interface ContainerWireframeProps {
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  status?: ContainerStatus;
}

const RIDGE_COUNT = 12;
const RIDGE_HEIGHT = 0.015;
const WALL_THICKNESS = 0.02;

function CorrugatedWall({
  width,
  height,
  color,
  position,
  rotation,
  opacity = 0.35,
}: {
  width: number;
  height: number;
  color: string;
  position: [number, number, number];
  rotation?: [number, number, number];
  opacity?: number;
}) {
  const ridges = useMemo(() => {
    const arr: { y: number }[] = [];
    const spacing = height / (RIDGE_COUNT + 1);
    for (let i = 1; i <= RIDGE_COUNT; i++) {
      arr.push({ y: -height / 2 + spacing * i });
    }
    return arr;
  }, [height]);

  return (
    <group position={position} rotation={rotation}>
      {/* Panel principal - semi-transparente */}
      <mesh castShadow receiveShadow>
        <planeGeometry args={[width, height]} />
        <meshStandardMaterial
          color={color}
          metalness={0.4}
          roughness={0.6}
          side={THREE.DoubleSide}
          transparent
          opacity={opacity}
          depthWrite={false}
        />
      </mesh>
      {/* Ridges corrugados - más opacos para dar estructura */}
      {ridges.map((r, i) => (
        <mesh key={i} position={[0, r.y, RIDGE_HEIGHT / 2]} castShadow>
          <boxGeometry args={[width, 0.008, RIDGE_HEIGHT]} />
          <meshStandardMaterial
            color={color}
            metalness={0.5}
            roughness={0.5}
            transparent
            opacity={opacity + 0.25}
          />
        </mesh>
      ))}
    </group>
  );
}

function Door({
  width,
  height,
  color,
  position,
  isOpen,
  side,
}: {
  width: number;
  height: number;
  color: string;
  position: [number, number, number];
  isOpen: boolean;
  side: 'left' | 'right';
}) {
  const doorWidth = width / 2;
  const openAngle = side === 'left' ? -1.2 : 1.2;
  const pivotX = side === 'left' ? -doorWidth / 2 : doorWidth / 2;

  return (
    <group position={position}>
      {/* Pivot point en el borde de la puerta */}
      <group position={[pivotX, 0, 0]} rotation={[0, isOpen ? openAngle : 0, 0]}>
        <group position={[-pivotX, 0, 0]}>
          {/* Panel de puerta */}
          <mesh castShadow receiveShadow>
            <boxGeometry args={[doorWidth - 0.01, height, WALL_THICKNESS]} />
            <meshStandardMaterial
              color={color}
              metalness={0.4}
              roughness={0.6}
              transparent
              opacity={isOpen ? 0.6 : 0.45}
              depthWrite={false}
            />
          </mesh>
          {/* Manija */}
          <mesh
            position={[
              side === 'left' ? doorWidth / 2 - 0.04 : -doorWidth / 2 + 0.04,
              0,
              WALL_THICKNESS / 2 + 0.008,
            ]}
            castShadow
          >
            <cylinderGeometry args={[0.006, 0.006, 0.12, 8]} />
            <meshStandardMaterial color="#71717a" metalness={0.7} roughness={0.3} />
          </mesh>
          {/* Ridges en puerta */}
          {Array.from({ length: 6 }).map((_, i) => {
            const spacing = height / 7;
            const y = -height / 2 + spacing * (i + 1);
            return (
              <mesh key={i} position={[0, y, WALL_THICKNESS / 2 + 0.005]} castShadow>
                <boxGeometry args={[doorWidth - 0.03, 0.006, 0.01]} />
                <meshStandardMaterial color={color} metalness={0.5} roughness={0.5} />
              </mesh>
            );
          })}
        </group>
      </group>
    </group>
  );
}

function Seal({ position }: { position: [number, number, number] }) {
  return (
    <mesh position={position}>
      <cylinderGeometry args={[0.015, 0.015, 0.04, 8]} />
      <meshStandardMaterial color="#ef4444" metalness={0.3} roughness={0.5} />
    </mesh>
  );
}

function ContainerBody({ lengthCm, widthCm, heightCm, status = 'EMPTY' }: ContainerWireframeProps) {
  const l = lengthCm / 100;
  const w = widthCm / 100;
  const h = heightCm / 100;
  const color = CONTAINER_STATUS_3D_COLORS[status];
  const doorsOpen = status === 'EMPTY' || status === 'LOADING';
  const showSeal = status === 'SEALED';

  // Color ligeramente más oscuro para interior
  const darkerColor = useMemo(() => {
    const c = new THREE.Color(color);
    c.multiplyScalar(0.7);
    return '#' + c.getHexString();
  }, [color]);

  return (
    <group>
      {/*
        Piso de madera, como el del portal del cliente.

        🔴 Era `#3f3f46` con algo de metalness: un piso casi negro. Y un
        contenedor real tiene piso de madera contrachapada — es lo que hace que
        la escena se lea como un contenedor y no como una caja de vidrio. Además,
        sobre negro las cajas oscuras desaparecían: no se distinguía si había
        carga en el fondo o estaba vacío.

        El mismo tono que usa `cargo-container-3d.tsx` en el portal, para que las
        dos pantallas muestren la misma carga con el mismo aspecto.
      */}
      <mesh position={[l / 2, 0, w / 2]} receiveShadow>
        <boxGeometry args={[l, WALL_THICKNESS, w]} />
        <meshStandardMaterial color="#b07a3f" roughness={0.85} metalness={0.02} />
      </mesh>
      {/* Las juntas de los tablones: una cada ~30 cm a lo largo. */}
      {Array.from({ length: Math.max(2, Math.round(l / 0.3)) }).map((_, i, arr) => (
        <mesh key={i} position={[((i + 0.5) * l) / arr.length, WALL_THICKNESS / 2 + 0.001, w / 2]}>
          <boxGeometry args={[0.006, 0.002, w]} />
          <meshStandardMaterial color="#8a5c2c" roughness={0.9} />
        </mesh>
      ))}

      {/* Techo - muy transparente para ver dentro desde arriba */}
      <mesh position={[l / 2, h, w / 2]} castShadow receiveShadow>
        <boxGeometry args={[l + 0.01, WALL_THICKNESS, w + 0.01]} />
        <meshStandardMaterial
          color={color}
          metalness={0.4}
          roughness={0.6}
          transparent
          opacity={0.15}
          depthWrite={false}
        />
      </mesh>

      {/* Pared izquierda (Z=0) */}
      <CorrugatedWall
        width={l}
        height={h}
        color={color}
        position={[l / 2, h / 2, 0]}
        rotation={[0, 0, 0]}
      />

      {/* Pared derecha (Z=w) */}
      <CorrugatedWall
        width={l}
        height={h}
        color={color}
        position={[l / 2, h / 2, w]}
        rotation={[0, Math.PI, 0]}
      />

      {/* Pared trasera (X=0) */}
      <CorrugatedWall
        width={w}
        height={h}
        color={darkerColor}
        position={[0, h / 2, w / 2]}
        rotation={[0, Math.PI / 2, 0]}
      />

      {/* Puertas frontales (X=l) */}
      <Door
        width={w}
        height={h}
        color={color}
        position={[l, h / 2, w / 2]}
        isOpen={doorsOpen}
        side="left"
      />
      <Door
        width={w}
        height={h}
        color={color}
        position={[l, h / 2, w / 2]}
        isOpen={doorsOpen}
        side="right"
      />

      {/* Sello rojo cuando está sellado */}
      {showSeal && <Seal position={[l + WALL_THICKNESS, h * 0.5, w / 2]} />}

      {/* Rieles inferiores (cross members) */}
      {[0.15, 0.5, 0.85].map((frac) => (
        <mesh key={frac} position={[l * frac, -0.01, w / 2]} castShadow>
          <boxGeometry args={[0.04, 0.025, w + 0.04]} />
          <meshStandardMaterial color="#27272a" metalness={0.5} roughness={0.6} />
        </mesh>
      ))}

      {/* Wireframe de referencia - contorno visible del contenedor */}
      <mesh position={[l / 2, h / 2, w / 2]}>
        <boxGeometry args={[l, h, w]} />
        <meshBasicMaterial transparent opacity={0} />
        <Edges color={color} lineWidth={1.5} />
      </mesh>

      {/* Esquineras (corner castings) */}
      {[
        [0, 0, 0],
        [l, 0, 0],
        [0, 0, w],
        [l, 0, w],
        [0, h, 0],
        [l, h, 0],
        [0, h, w],
        [l, h, w],
      ].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]} castShadow>
          <boxGeometry args={[0.05, 0.05, 0.05]} />
          <meshStandardMaterial color="#52525b" metalness={0.6} roughness={0.4} />
        </mesh>
      ))}
    </group>
  );
}

export function ContainerWireframe(props: ContainerWireframeProps) {
  const { status = 'EMPTY' } = props;

  // Para IN_TRANSIT, agregar movimiento sutil como en el mar
  if (status === 'IN_TRANSIT') {
    return (
      <Float
        speed={1.5}
        rotationIntensity={0.15}
        floatIntensity={0.3}
        floatingRange={[-0.03, 0.03]}
      >
        <ContainerBody {...props} />
      </Float>
    );
  }

  return <ContainerBody {...props} />;
}
